import {useMutation, useQuery} from '@apollo/client';
import {Empty, Pagination, Spin, Table} from 'antd';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';

import {
  CREATE_CASH_REWARD_BANK,
  UPDATE_CASH_REWARD_BANK,
} from 'graphql/mutations/rewardScheme';
import {GET_ALL_CASH_BANKS} from 'graphql/queries/rewardScheme';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {walletCurrencies} from 'utils/mock';
import RewardBankModal from './RewardBankModal';
import {Remaining, readMutation, statOf} from './rewardBankShared';
import styles from './rewardBanks.module.scss';

const CashRewardBank = () => {
  const [modal, setModal] = useState({
    open: false,
    mode: 'CREATE',
    record: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.RewardBanks),
  );

  const {loading, refetch, data} = useQuery(GET_ALL_CASH_BANKS, {
    variables: {page: 1, limit: 10},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  const [createCashRewardBank, {loading: creating}] = useMutation(
    CREATE_CASH_REWARD_BANK,
  );
  const [updateCashRewardBank, {loading: updating}] = useMutation(
    UPDATE_CASH_REWARD_BANK,
  );

  const result = data?.getAllCashBanks;
  const banks = result?.data?.cashRewardBank ?? [];
  const stats = result?.data?.cashStats;
  const total = result?.pagination?.total ?? banks.length;

  const closeModal = () => setModal(current => ({...current, open: false}));

  const handleSubmit = async values => {
    const isEdit = modal.mode === 'EDIT';
    // Every path goes through readMutation: the old handlers destructured
    // `data` straight out of the response, so a GraphQL error threw inside the
    // promise and the screen simply sat there.
    const outcome = await readMutation(
      isEdit
        ? updateCashRewardBank({
            variables: {
              data: {
                cashBankId: modal.record?.id,
                amountDeposited: values.amount,
              },
            },
          })
        : createCashRewardBank({
            variables: {
              data: {
                amountDeposited: values.amount,
                currency: values.currency,
              },
            },
          }),
      isEdit ? 'updateCashRewardBank' : 'createCashRewardBank',
    );

    if (!outcome.ok) return toast.error(outcome.message);
    toast.success(isEdit ? 'Cash account updated' : 'Cash account created');
    closeModal();
    refetch();
  };

  const columns = [
    {
      title: 'Currency',
      key: 'currency',
      render: (_, record) => (
        <span className={styles.tickerSymbol}>{record?.currency ?? '—'}</span>
      ),
    },
    {
      title: 'Deposited',
      key: 'amountDeposited',
      render: (_, record) => (
        <span className={styles.money}>
          {Number(record?.amountDeposited ?? 0).toLocaleString('en-US')}
        </span>
      ),
    },
    {
      title: 'Disbursed',
      key: 'amountDisbursed',
      render: (_, record) => (
        <span className={styles.moneyMuted}>
          {Number(record?.amountDisbursed ?? 0).toLocaleString('en-US')}
        </span>
      ),
    },
    {
      title: 'Left',
      key: 'amountLeft',
      render: (_, record) => (
        <Remaining
          used={record?.amountDisbursed}
          total={record?.amountDeposited}
          left={record?.amountLeft}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <button
          type="button"
          className={styles.rowAction}
          disabled={!tabOperations?.update}
          onClick={() =>
            setModal({
              open: true,
              mode: 'EDIT',
              record: {...record, label: `${record?.currency} account`},
            })
          }>
          Top up
        </button>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Cash reward bank</h1>
          <p className={styles.subtitle}>
            Cash set aside per currency to pay out rewards. Each account is
            drawn down as rewards are given.
          </p>
        </div>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={!tabOperations?.create}
          onClick={() => setModal({open: true, mode: 'CREATE', record: null})}>
          Create cash account
        </button>
      </div>

      {/* Only what the service returns. `cashStats` carries this one figure
          and no totals — and a "total deposited" cannot be computed here
          anyway, since each account is a different currency and adding them
          produces a number that means nothing. */}
      <div className={styles.stats}>
        {statOf(
          'New users from cash rewards',
          stats?.totalNewUsersFromRewards ?? 0,
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading && !banks.length ? (
          <div className={styles.loading}>
            <Spin size="large" />
            <span>Loading cash accounts…</span>
          </div>
        ) : (
          <>
            <Table
              rowKey={record => record?.id}
              columns={columns}
              dataSource={banks}
              loading={loading}
              pagination={false}
              scroll={{x: 'max-content'}}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No cash accounts yet. Create one to start funding cash rewards."
                  />
                ),
              }}
            />
            {total > limit && (
              <Pagination
                className="ant-table-pagination"
                current={currentPage}
                pageSize={limit}
                total={total}
                showSizeChanger
                showQuickJumper
                onChange={(page, size) => {
                  // Size and page both arrive here. Handling the size change
                  // in onShowSizeChange as well meant the page reset was
                  // overwritten a moment later by this callback.
                  const nextLimit = size ?? limit;
                  const nextPage = nextLimit !== limit ? 1 : page;
                  setLimit(nextLimit);
                  setCurrentPage(nextPage);
                  refetch({page: nextPage, limit: nextLimit});
                }}
              />
            )}
          </>
        )}
      </div>

      <RewardBankModal
        isOpen={modal.open}
        mode={modal.mode}
        kind="CASH"
        record={modal.record}
        options={walletCurrencies.map(({key, name}) => ({
          value: key,
          label: `${key} — ${name}`,
        }))}
        saving={creating || updating}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export {CashRewardBank};
